import React, { useEffect, useState } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import appConfig from "../../config/appConfig";
import { verifyPermission } from "../../helpers/permission";
import { verifyTokenExpire } from "../../middleware/verifyToken";
import { ERROR_401 } from "../../config/errorPage";
import LoadingTable from "../components/LoadingTable";
import InfirmationTable from "../components/InfirmationTable";
import { ToastBottomEnd } from "../components/Toast";
import {
  toastSuccessMessageWithParameterConfig,
  toastErrorMessageWithParameterConfig,
} from "../../config/toastConfig";

const ConfigRelanceClaimer = (props) => {
  const { t, ready } = useTranslation();
  document.title = "Satis client - " + (ready ? t("Configuration des relances réclamant") : "");

  // Vérification de la permission
  if (!verifyPermission(props.userPermissions, "update-claimer-follow-up-config")) {
    window.location.href = ERROR_401;
  }

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [limit, setLimit] = useState(3);
  const [postDelay, setPostDelay] = useState(15);
  const [delay, setDelay] = useState(15);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = () => {
    if (verifyTokenExpire()) {
      setLoading(true);
      axios
        .get(`${appConfig.apiDomaine}/configurations/claimer-follow-up-config`)
        .then((response) => {
          setLoading(false);
          if (response.data) {
            setLimit(response.data.limit || 3);
            setPostDelay(response.data.post_delay || 15);
            setDelay(response.data.delay || 15);
          }
        })
        .catch((error) => {
          setLoading(false);
          ToastBottomEnd.fire(
            toastErrorMessageWithParameterConfig(
              t("Impossible de charger la configuration actuelle")
            )
          );
        });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    if (verifyTokenExpire()) {
      setSubmitting(true);
      const data = {
        limit: parseInt(limit, 10),
        post_delay: parseInt(postDelay, 10),
        delay: parseInt(delay, 10),
      };

      axios
        .put(`${appConfig.apiDomaine}/configurations/claimer-follow-up-config`, data)
        .then((response) => {
          setSubmitting(false);
          ToastBottomEnd.fire(
            toastSuccessMessageWithParameterConfig(
              t("Configuration mise à jour avec succès")
            )
          );
        })
        .catch((error) => {
          setSubmitting(false);
          if (error.response && error.response.data && error.response.data.errors) {
            setErrors(error.response.data.errors);
          } else {
            ToastBottomEnd.fire(
              toastErrorMessageWithParameterConfig(
                t("Erreur lors de la mise à jour de la configuration")
              )
            );
          }
        });
    }
  };

  if (!ready) return null;

  return (
    <div
      className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      {/* En-tête Fil d'ariane */}
      <div className="kt-subheader kt-grid__item" id="kt_subheader">
        <div className="kt-container kt-container--fluid">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">{t("Configuration")}</h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              <a href="#home" className="kt-subheader__breadcrumbs-home">
                <i className="flaticon2-shelter" />
              </a>
              <span className="kt-subheader__breadcrumbs-separator" />
              <span className="kt-subheader__breadcrumbs-link">
                {t("Relances réclamant & Déclassements")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        <InfirmationTable
          information={t(
            "Paramétrage des limites de relances et des délais de déclassement des réclamations incomplètes"
          )}
        />

        <div className="kt-portlet">
          <div className="kt-portlet__head">
            <div className="kt-portlet__head-label">
              <h3 className="kt-portlet__head-title">
                {t("Limites et Délais de relance")}
              </h3>
            </div>
          </div>

          {loading ? (
            <LoadingTable />
          ) : (
            <form className="kt-form" onSubmit={handleSubmit}>
              <div className="kt-portlet__body">
                <div className="row">
                  {/* Nombre max de relances */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="font-weight-bold">
                        {t("Nombre max de relances")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        className={`form-control ${
                          errors.limit ? "is-invalid" : ""
                        }`}
                        value={limit}
                        onChange={(e) => setLimit(e.target.value)}
                        required
                      />
                      <small className="form-text text-muted">
                        {t(
                          "Nombre de relances requises avant le déclassement temporaire."
                        )}
                      </small>
                      {errors.limit && (
                        <div className="invalid-feedback">
                          {errors.limit[0]}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Délai entre les relances */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="font-weight-bold">
                        {t("Délai de relance en jours ")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        className={`form-control ${
                          errors.delay ? "is-invalid" : ""
                        }`}
                        value={delay}
                        onChange={(e) => setDelay(e.target.value)}
                        required
                      />
                      <small className="form-text text-muted">
                        {t("Délai indicatif entre deux relances.")}
                      </small>
                      {errors.delay && (
                        <div className="invalid-feedback">
                          {errors.delay[0]}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Délai de grâce post-déclassement */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="font-weight-bold">
                        {t("Délai de grâce en jours ")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        className={`form-control ${
                          errors.post_delay ? "is-invalid" : ""
                        }`}
                        value={postDelay}
                        onChange={(e) => setPostDelay(e.target.value)}
                        required
                      />
                      <small className="form-text text-muted">
                        {t(
                          "Jours après déclassement temporaire avant déclassement définitif."
                        )}
                      </small>
                      {errors.post_delay && (
                        <div className="invalid-feedback">
                          {errors.post_delay[0]}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="kt-portlet__foot">
                <div className="kt-form__actions text-right">
                  <button
                    type="submit"
                    className="btn btn-primary font-weight-bold"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {t("Enregistrement...")}
                      </>
                    ) : (
                      <>
                        <i className="la la-check mr-1"></i>
                        {t("Enregistrer les modifications")}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userPermissions: state.user.user.permissions,
});

export default connect(mapStateToProps)(ConfigRelanceClaimer);